import { useManualQuery } from "graphql-hooks";
import { useQuery, useMutation } from "@tanstack/react-query";

const GET_CALCULATE = `
  query GetPriceBreakdownByAsin($asin: String!) {
  getPriceBreakdownByAsin(asin: $asin){
    amazonPrice
    fee
    tax
    amazonPriceTax
    commission
    earnings
    shipping
    total
    weightKg
    totalUYU
    totalUYURounded
  }
}
`;

const GET_CALCULATE_FROM_SALE_REPORT = `
  query GetCalculatedPriceBreakdownFromSaleReport($amazonPrice: Float!, $weight: Float!) {
    getCalculatedPriceBreakdownFromSaleReport(amazonPrice: $amazonPrice, weight: $weight) {
      amazonPrice
      fee
      tax
      amazonPriceTax
      commission
      earnings
      shipping
      total
      weightKg
      totalUYU
      totalUYURounded
    }
  }
`;

export interface PriceBreakdown {
  amazonPrice: number;
  fee: number;
  tax: number;
  amazonPriceTax: number;
  commission: number;
  earnings: number;
  shipping: number;
  total: number;
  weightKg: number;
  totalUYU: number;
  totalUYURounded: number;
}

// Función para hacer la petición GraphQL
const fetchCalculateData = async (variables: {
  asin?: string;
}) => {
  const sessionData = localStorage.getItem("session");
  const token = sessionData ? JSON.parse(sessionData).sessionInfo?.token : null;

  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: JSON.stringify({
      query: GET_CALCULATE,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || "GraphQL error");
  }

  return result.data.getPriceBreakdownByAsin;
};

const fetchCalculateFromSaleReportData = async (variables: {
  amazonPrice: number;
  weight: number;
}) => {
  const sessionData = localStorage.getItem("session");
  const token = sessionData ? JSON.parse(sessionData).sessionInfo?.token : null;

  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: JSON.stringify({
      query: GET_CALCULATE_FROM_SALE_REPORT,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || "GraphQL error");
  }

  return result.data.getCalculatedPriceBreakdownFromSaleReport as PriceBreakdown;
};

// Hook optimizado con TanStack Query
export const useCalculate = (asin?: string) => {
  return useQuery({
    queryKey: ["calculate", asin],
    queryFn: () => fetchCalculateData({ asin }),
    enabled: !!asin, // Solo ejecutar si hay asin
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (renombrado de cacheTime en v5)
  });
};

export const useCalculateFromSaleReport = (
  amazonPrice?: number | null,
  weight?: number | null
) => {
  const parsedPrice = Number(amazonPrice);
  const parsedWeight = Number(weight);
  const hasValidInputs =
    amazonPrice != null &&
    weight != null &&
    !Number.isNaN(parsedPrice) &&
    !Number.isNaN(parsedWeight);

  return useQuery({
    queryKey: ["calculateSaleReport", parsedPrice, parsedWeight],
    queryFn: () =>
      fetchCalculateFromSaleReportData({
        amazonPrice: parsedPrice,
        weight: parsedWeight,
      }),
    enabled: hasValidInputs,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook para mutaciones (cuando necesitas enviar datos)
export const useCalculateMutation = () => {
  return useMutation({
    mutationFn: fetchCalculateData,
    onError: (error) => {
      console.error("Error en cálculo:", error);
    },
  });
};

// Hook original para mantener compatibilidad
export const useCalculateManual = () => {
  const [getCalculate, { loading, data }] = useManualQuery(GET_CALCULATE);

  return { getCalculate, loading, data };
};
